// src/pages/EditDriver.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Collapse,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchDriver, updateDriver, deleteDriver } from '../api/drivers';
import useVehicles from '../hooks/useVehicles';
import { Autocomplete } from '@mui/material';

import {
  getDocuments,
  uploadDocument,
  deleteDocument,
  downloadDocument,
} from '../api/documents';

export default function EditDriver() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { vehicles, loading: vehiclesLoading } = useVehicles();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [driver, setDriver] = useState(null);

  const [errors, setErrors] = useState({});
  const [genericError, setGenericError] = useState('');

  // Delete driver dialog state (type "confirm")
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Documents state (for this driver)
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const fileInputRef = useRef(null);

  // File delete dialog (type "confirm")
  const [fileDeleteOpen, setFileDeleteOpen] = useState(false);
  const [fileDeleteInput, setFileDeleteInput] = useState('');
  const [fileDeleteLoading, setFileDeleteLoading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [uploadErrors, setUploadErrors] = useState({});

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const docs = await getDocuments('driver', id);
      setDocuments(docs || []);
    } catch (err) {
      console.error('Failed to load driver documents:', err);
      // optional: setGenericError('Failed to load documents.');
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    const doFetch = async () => {
      try {
        const d = await fetchDriver(id);
        setDriver({
          firstname: d.firstname || '',
          lastname: d.lastname || '',
          phone: d.phone || '',
          role: d.role || 'DRIVER',
          vehicle: d.vehicle || '',
          address: {
            line_1: d.address?.line_1 || '',
            line_2: d.address?.line_2 || '',
            town: d.address?.town || '',
            county: d.address?.county || '',
            postal_code: d.address?.postal_code || '',
            country: d.address?.country || 'UK',
          },
        });
        setLoading(false);
      } catch (err) {
        console.error('Failed to load driver:', err);
        alert('Failed to load driver');
        navigate('/drivers');
      }
    };

    doFetch();
    loadDocuments();
  }, [id, navigate]);

  // Keep editable file name in sync with selected file
  useEffect(() => {
    setUploadFileName(uploadFile ? uploadFile.name.split('.')[0] : '');
  }, [uploadFile]);

  const updateField = (field, value) =>
    setDriver((prev) => ({ ...prev, [field]: value }));

  const updateAddress = (field, value) =>
    setDriver((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));

  const clearFieldError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const submit = async () => {
    if (!driver) return;
    setSaving(true);
    setErrors({});
    setGenericError('');
    try {
      await updateDriver(id, driver);
      navigate('/drivers');
    } catch (err) {
      console.error('Failed to update driver:', err);
      if (err.status === 400 && err.validationErrors) {
        setErrors(err.validationErrors || {});
      } else {
        setGenericError(
          'A server error occurred while updating the driver. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = () => {
    setDeleteInput('');
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    if (deleteLoading) return;
    setDeleteOpen(false);
    setDeleteInput('');
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteDriver(id);
      navigate('/drivers');
    } catch (err) {
      console.error('Failed to delete driver:', err);
      alert('Failed to delete driver');
    } finally {
      setDeleteLoading(false);
      setDeleteOpen(false);
    }
  };

  const deleteDisabled =
    !deleteInput.trim() ||
    deleteInput.trim().toLowerCase() !== 'confirm';

  // Document actions
  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file);
    setUploadModalOpen(true);
  };

  const handleDownload = (docId) => {
    downloadDocument(docId);
  };

  const handleRequestDeleteDoc = (doc) => {
    setFileToDelete(doc);
    setFileDeleteInput('');
    setFileDeleteOpen(true);
  };

  const handleCloseFileDelete = () => {
    if (fileDeleteLoading) return;
    setFileDeleteOpen(false);
    setFileToDelete(null);
    setFileDeleteInput('');
  };

  const handleConfirmFileDelete = async () => {
    if (!fileToDelete) return;
    setFileDeleteLoading(true);
    try {
      await deleteDocument(fileToDelete._id);
      await loadDocuments();
    } catch (err) {
      console.error('Failed to delete document:', err);
      setGenericError('Failed to delete document. Please try again.');
    } finally {
      setFileDeleteLoading(false);
      setFileDeleteOpen(false);
      setFileToDelete(null);
      setFileDeleteInput('');
    }
  };

  const fileDeleteDisabled =
    !fileToDelete ||
    !fileDeleteInput.trim() ||
    fileDeleteInput.trim().toLowerCase() !== 'confirm';

  if (loading || !driver)
    return (
      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );

  const selectedVehicle =
    vehicles.find((v) => v.id === driver.vehicle) || null;

  return (
    <Box sx={{ position: 'relative', pb: 8 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Driver Details
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            View or modify the driver’s details, vehicle assignment, and related
            documents.
          </Typography>
        </Box>
      </Box>

      {genericError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGenericError('')}
        >
          {genericError}
        </Alert>
      )}

      <Paper
        elevation={2}
        sx={{
          p: 3,
          borderRadius: 2,
          mx: 'auto',
        }}
      >
        {/* PERSONAL INFORMATION */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Personal Information
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="First Name"
                fullWidth
                value={driver.firstname}
                onChange={(e) => {
                  updateField('firstname', e.target.value);
                  clearFieldError('firstname');
                }}
                error={!!errors.firstname}
                helperText={errors.firstname}
              />
            ) : (
              <ReadOnlyField label="First Name" value={driver.firstname} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Last Name"
                fullWidth
                value={driver.lastname}
                onChange={(e) => {
                  updateField('lastname', e.target.value);
                  clearFieldError('lastname');
                }}
                error={!!errors.lastname}
                helperText={errors.lastname}
              />
            ) : (
              <ReadOnlyField label="Last Name" value={driver.lastname} />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* CONTACT DETAILS */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Contact Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <TextField
                label="Phone Number"
                fullWidth
                value={driver.phone}
                onChange={(e) => {
                  updateField('phone', e.target.value);
                  clearFieldError('phone');
                }}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            ) : (
              <ReadOnlyField label="Phone Number" value={driver.phone} />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ADDRESS */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Address
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12 }}>
            {editMode ? (
              <TextField
                label="Address Line 1"
                fullWidth
                value={driver.address.line_1}
                onChange={(e) => {
                  updateAddress('line_1', e.target.value);
                  clearFieldError('address.line_1');
                }}
                error={!!errors['address.line_1']}
                helperText={errors['address.line_1']}
              />
            ) : (
              <ReadOnlyField
                label="Address Line 1"
                value={driver.address.line_1}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12 }}>
            {editMode ? (
              <TextField
                label="Address Line 2"
                fullWidth
                value={driver.address.line_2}
                onChange={(e) => {
                  updateAddress('line_2', e.target.value);
                  clearFieldError('address.line_2');
                }}
                error={!!errors['address.line_2']}
                helperText={errors['address.line_2']}
              />
            ) : (
              <ReadOnlyField
                label="Address Line 2"
                value={driver.address.line_2}
              />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Town"
                fullWidth
                value={driver.address.town}
                onChange={(e) => {
                  updateAddress('town', e.target.value);
                  clearFieldError('address.town');
                }}
                error={!!errors['address.town']}
                helperText={errors['address.town']}
              />
            ) : (
              <ReadOnlyField label="Town" value={driver.address.town} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="County"
                fullWidth
                value={driver.address.county}
                onChange={(e) => {
                  updateAddress('county', e.target.value);
                  clearFieldError('address.county');
                }}
                error={!!errors['address.county']}
                helperText={errors['address.county']}
              />
            ) : (
              <ReadOnlyField label="County" value={driver.address.county} />
            )}
          </Grid>

          <Grid item size={{ xs: 12, sm: 4 }}>
            {editMode ? (
              <TextField
                label="Postal Code"
                fullWidth
                value={driver.address.postal_code}
                onChange={(e) => {
                  updateAddress('postal_code', e.target.value);
                  clearFieldError('address.postal_code');
                }}
                error={!!errors['address.postal_code']}
                helperText={errors['address.postal_code']}
              />
            ) : (
              <ReadOnlyField
                label="Postal Code"
                value={driver.address.postal_code}
              />
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* VEHICLE ASSIGNMENT */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Vehicle Assignment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            {editMode ? (
              <Autocomplete
                fullWidth
                options={vehicles}
                loading={vehiclesLoading}
                value={selectedVehicle}
                onChange={(e, newValue) => {
                  updateField('vehicle', newValue ? newValue.id : '');
                  clearFieldError('vehicle');
                }}
                getOptionLabel={(option) => option.vrm || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign Vehicle (optional)"
                    fullWidth
                    error={!!errors.vehicle}
                    helperText={errors.vehicle}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vehiclesLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <ReadOnlyField
                label="Assigned Vehicle"
                value={
                  selectedVehicle
                    ? `${selectedVehicle.vrm} – ${
                        selectedVehicle.make_model ||
                        selectedVehicle.make ||
                        ''
                      }`
                    : 'Unassigned'
                }
              />
            )}
          </Grid>
        </Grid>

        {/* ACTION BUTTONS */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
          }}
        >
          {/* Left: delete driver */}
          <Button
            color="error"
            variant="outlined"
            sx={{ textTransform: 'none' }}
            onClick={handleOpenDelete}
          >
            Delete Driver
          </Button>

          {/* Right: close + save */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/drivers')}
              sx={{ textTransform: 'none' }}
            >
              Close
            </Button>

            <Collapse in={editMode}>
              <Button
                variant="contained"
                sx={{ textTransform: 'none', ml: 2 }}
                onClick={submit}
                disabled={saving}
              >
                {saving ? <CircularProgress size={22} /> : 'Save Changes'}
              </Button>
            </Collapse>
          </Box>
        </Box>
      </Paper>

      {/* Documents panel */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Documents
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
            >
              Upload
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Box>

          {documentsLoading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              {(!documents || documents.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  No documents uploaded for this driver.
                </Typography>
              )}

              <List dense>
                {documents.map((doc) => {
                  const name =
                    doc.filename || doc.originalName || 'Document';
                  const typeLabel = doc.document_type
                    ? doc.document_type.replace(/_/g, ' ')
                    : 'Unknown';
                  const sizeLabel = doc.size
                    ? `${(doc.size / 1024).toFixed(1)} KB`
                    : '—';
                  const mimeLabel = doc.mime_type || '—';

                  return (
                    <ListItem
                      key={doc._id}
                      sx={{
                        px: 0,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 500 }}
                        >
                          {name}
                        </Typography>

                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={typeLabel}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {sizeLabel}
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            •
                          </Typography>

                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {mimeLabel}
                          </Typography>
                        </Box>
                      </Box>

                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDownload(doc._id)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          sx={{ ml: 1 }}
                          onClick={() => handleRequestDeleteDoc(doc)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Paper>
      </Box>

      {/* Floating Edit FAB */}
      <Fab
        color={editMode ? 'default' : 'primary'}
        onClick={() => setEditMode((prev) => !prev)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          boxShadow: 4,
        }}
      >
        <EditIcon />
      </Fab>

      {/* Delete Driver dialog – requires typing "confirm" */}
      <Dialog
        open={deleteOpen}
        onClose={handleCloseDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Driver</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete this driver and remove any
              association with vehicles or hire agreements. To confirm, please
              type <strong>confirm</strong> in the box below. This action
              cannot be undone.
            </Typography>
            <TextField
              label='Type "confirm" to delete'
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteDisabled || deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete document dialog (type "confirm") */}
      <Dialog
        open={fileDeleteOpen}
        onClose={handleCloseFileDelete}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Delete Document
          {fileToDelete && fileToDelete.filename
            ? ` – ${fileToDelete.filename}`
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              This will permanently delete the selected document for this
              driver. To confirm, please type <strong>confirm</strong> in the
              box below.
            </Typography>
            <TextField
              label='Type "confirm" to delete'
              value={fileDeleteInput}
              onChange={(e) => setFileDeleteInput(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFileDelete} disabled={fileDeleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmFileDelete}
            disabled={fileDeleteDisabled || fileDeleteLoading}
          >
            {fileDeleteLoading ? (
              <CircularProgress size={20} />
            ) : (
              'Confirm Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload document dialog */}
      <Dialog
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadErrors({});
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Driver Document</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              Uploading: <strong>{uploadFile?.name}</strong>
            </Typography>

            <TextField
              required
              label="File Name"
              type="text"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={uploadFileName}
              onChange={(e) => setUploadFileName(e.target.value)}
              error={!!uploadErrors.filename}
              helperText={uploadErrors.filename}
            />

            <TextField
              required
              label="Document Type"
              select
              fullWidth
              value={uploadType}
              onChange={(e) => {
                setUploadType(e.target.value);
                setUploadErrors((p) => ({
                  ...p,
                  document_type: undefined,
                }));
              }}
              error={!!uploadErrors.document_type}
              helperText={uploadErrors.document_type}
            >
              <MenuItem value="driver_license">Driver License</MenuItem>
              <MenuItem value="driver_private_hire_badge">Driver Private Hire Badge</MenuItem>
              <MenuItem value="driver_hackney_carraige_badge">Driver Hackney Carriage Badge</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              label="Document Expiry Date (optional)"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={uploadExpiry}
              onChange={(e) => setUploadExpiry(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUploadModalOpen(false);
              setUploadErrors({});
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={async () => {
              const errs = {};
              if (!uploadFileName.trim()) {
                errs.filename = 'File name is required';
              }
              if (!uploadType) {
                errs.document_type = 'Document type is required';
              }

              if (Object.keys(errs).length > 0) {
                setUploadErrors(errs);
                return;
              }

              try {
                const formData = new FormData();
                formData.append('file', uploadFile);
                const originalParts = uploadFile.name.split('.');
                const ext =
                  originalParts.length > 1
                    ? originalParts[originalParts.length - 1]
                    : '';
                const finalName = ext
                  ? `${uploadFileName}.${ext}`
                  : uploadFileName;

                formData.append('filename', finalName);
                formData.append('entity_type', 'driver');
                formData.append('entity_id', id);
                formData.append('document_type', uploadType);
                if (uploadExpiry) {
                  formData.append('expiry_date', uploadExpiry);
                }

                await uploadDocument(formData);

                setUploadModalOpen(false);
                setUploadErrors({});
                setUploadFile(null);
                setUploadFileName('');
                setUploadType('');
                setUploadExpiry('');

                await loadDocuments();
              } catch (err) {
                console.error('Failed to upload driver document:', err);
                setGenericError(
                  'Failed to upload document. Please try again.'
                );
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        variant="caption"
        sx={{ textTransform: 'uppercase', color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}
